import { DerbyRngV1 } from '../rng';
import { RACE_TICK_S, RACE_TUNING } from './tuning';
import type {
  AiExecution,
  AiPolicyId,
  HorseParams,
  HorseStats,
  RaceOpponent,
  RaceRules,
  RaceSetup,
} from './types';

export const STAT_MIN = 1;
export const STAT_MAX = 20;

export function clampStat(level: number): number {
  return Math.max(STAT_MIN, Math.min(STAT_MAX, Math.round(level)));
}

/** The single seam from trained stats to race physics — all linear so
 * tuning stays legible. */
export function statsToParams(stats: HorseStats): HorseParams {
  const t = RACE_TUNING;
  const speed = clampStat(stats.speed);
  const stamina = clampStat(stats.stamina);
  const accel = clampStat(stats.accel);
  const heart = clampStat(stats.heart);
  return {
    vBase: t.vBase,
    vExhausted: t.vExhausted,
    vMax: t.vMaxBase + t.vMaxPerSpeed * speed,
    tapImpulse: t.tapImpulseBase + t.tapImpulsePerAccel * accel,
    dragK: t.dragK,
    exhaustedDragK: t.exhaustedDragK,
    exhaustedRegenMul: t.exhaustedRegenMul,
    staminaMax: t.staminaBase + t.staminaPerStamina * stamina,
    tapCost: t.tapCost,
    regenPerS: t.regenBase + t.regenPerHeart * heart,
    regenDelayS: t.regenDelayS,
    gateAccel: t.gateAccelBase + t.gateAccelPerAccel * accel,
    gateBoostS: t.gateBoostS,
    noFlinch: false,
    frontRunner: false,
    closer: false,
  };
}

/** Race rules snapshot from the live tuning object. */
export function currentRules(): RaceRules {
  const t = RACE_TUNING;
  return {
    tickS: RACE_TICK_S,
    countdownS: t.countdownS,
    perfectWindowS: t.perfectWindowS,
    minTapIntervalS: t.minTapIntervalS,
    flinchSuppressS: t.flinchSuppressS,
    perfectGateMul: t.perfectGateMul,
    recoverFraction: t.recoverFraction,
    timeoutS: t.timeoutS,
  };
}

/** Long-run tap cadence the tank can sustain: regen balances spend,
 * with each tap also parking regen for the delay window. */
export function sustainableTapRate(params: HorseParams): number {
  return params.regenPerS / (params.tapCost + params.regenPerS * params.regenDelayS);
}

/** Mean-field steady pace at a given cadence: impulse inflow balances drag.
 * r·I·(1−v/vMax) = k·(v−vBase)  ⇒  v = (k·vBase + r·I)/(k + r·I/vMax). */
export function equilibriumPace(params: HorseParams, tapRate = sustainableTapRate(params)): number {
  const inflow = tapRate * params.tapImpulse;
  return (params.dragK * params.vBase + inflow) / (params.dragK + inflow / params.vMax);
}

/**
 * Semi-analytic lower bound on finish time over ALL tap policies:
 * steady pace is capped by the sustainable-cadence equilibrium, and the
 * starting tank + gate phase are credited as generous one-off distance
 * bonuses. This is the "stats bound the outcome" contract — if a tuning
 * change lets a weaker horse beat a stronger league, the bounds test
 * fails before any playtest does.
 */
export function theoreticalBestTime(params: HorseParams, distanceM: number): number {
  const veq = equilibriumPace(params);
  const softCap = Math.max(0.05, 1 - veq / params.vMax);
  // Dumping the full tank is worth at most its decay integral at race pace.
  const dumpBonusM = (params.staminaMax / params.tapCost) * params.tapImpulse * softCap / params.dragK;
  // Gate thrust integrates to at most accel·duration·perfectMul of impulse.
  const gateBonusM =
    (params.gateAccel * params.gateBoostS * RACE_TUNING.perfectGateMul) / params.dragK;
  const steadyTime = Math.max(0, distanceM - dumpBonusM - gateBonusM) / veq;
  return Math.max(steadyTime, distanceM / params.vMax, 1);
}

export interface LeagueDef {
  index: number;
  id: string;
  name: string;
  distanceM: number;
  medianLevel: number;
  /** Per-stat spread drawn around the median, inclusive. */
  statSpread: [number, number];
  exec: AiExecution;
  /** One policy per opponent lane, in order. */
  policyMix: AiPolicyId[];
}

export const LEAGUE_DEFS: readonly LeagueDef[] = [
  {
    index: 0,
    id: 'local',
    name: 'Local',
    distanceM: 300,
    medianLevel: 3,
    statSpread: [-2, 2],
    exec: { cadenceMul: 1.05, jitter: 0.45, perfectChance: 0.15, flinchChance: 0.15 },
    policyMix: ['pacer', 'sprinter', 'closer', 'lazy'],
  },
  {
    index: 1,
    id: 'regional',
    name: 'Regional',
    distanceM: 400,
    medianLevel: 10,
    statSpread: [-3, 2],
    exec: { cadenceMul: 1.15, jitter: 0.25, perfectChance: 0.3, flinchChance: 0.08 },
    policyMix: ['pacer', 'sprinter', 'closer', 'pacer'],
  },
  {
    index: 2,
    id: 'national',
    name: 'National',
    distanceM: 500,
    medianLevel: 16,
    statSpread: [-3, 2],
    exec: { cadenceMul: 1.25, jitter: 0.12, perfectChance: 0.5, flinchChance: 0.03 },
    policyMix: ['pacer', 'closer', 'sprinter', 'pacer'],
  },
  {
    // The capstone, not a ladder rung: a cap-20 horse ridden well should
    // win this — so it sits outside the analytic bounding contract.
    index: 3,
    id: 'derby',
    name: 'Grand Derby',
    distanceM: 550,
    medianLevel: 18,
    statSpread: [-2, 1],
    exec: { cadenceMul: 1.3, jitter: 0.08, perfectChance: 0.6, flinchChance: 0.02 },
    policyMix: ['pacer', 'closer', 'pacer', 'sprinter'],
  },
];

/** The graded ladder — the "stats bound the outcome" contract applies to
 * adjacent rungs here; the Derby capstone is excluded by design. */
export const LADDER_LEAGUES = 3;

const RIVAL_NAMES = [
  'Copper Bell',
  'Midnight Ledger',
  'Prairie Comet',
  'Old Tobacco',
  'Silver Furlong',
  'Dust Devil',
  'Honest Abacus',
  'Clover Gambit',
  'Iron Meadow',
  'Whistling Jenny',
  'Barnstormer',
  'Quiet Thunder',
] as const;

export function medianStats(level: number): HorseStats {
  return { speed: level, stamina: level, accel: level, heart: level };
}

/** Draw an opponent field for a league. Deterministic per rng state. */
export function buildLeagueField(league: LeagueDef, rng: DerbyRngV1): RaceOpponent[] {
  const names = [...RIVAL_NAMES];
  const opponents: RaceOpponent[] = [];
  for (const policy of league.policyMix) {
    const [lo, hi] = league.statSpread;
    const stats: HorseStats = {
      speed: clampStat(league.medianLevel + rng.integer(lo, hi)),
      stamina: clampStat(league.medianLevel + rng.integer(lo, hi)),
      accel: clampStat(league.medianLevel + rng.integer(lo, hi)),
      heart: clampStat(league.medianLevel + rng.integer(lo, hi)),
    };
    const nameIndex = rng.integer(0, names.length - 1);
    const name = names.splice(nameIndex, 1)[0] ?? 'Outsider';
    opponents.push({ params: statsToParams(stats), policy, name });
  }
  return opponents;
}

/** Convenience used by the graybox scene and the test harness. */
export function makeRaceSetup(
  league: LeagueDef,
  playerStats: HorseStats,
  seed: number,
  options: { distanceM?: number; playerName?: string } = {},
): RaceSetup {
  const rng = new DerbyRngV1(seed).fork('field');
  return {
    seed,
    distanceM: options.distanceM ?? league.distanceM,
    playerName: options.playerName ?? 'You',
    player: statsToParams(playerStats),
    opponents: buildLeagueField(league, rng),
    exec: league.exec,
    rules: currentRules(),
  };
}
